import type { CSSProperties } from 'react';
import { PROTOCOL_THEME } from '../protocolTheme';

/** Semantic chip accents — borders/glows match meaning (220В / АКБ / colors / protocols). */

export type ConfiguratorAccentKey =
  | 'tier-standard'
  | 'tier-premium'
  | 'power-wired'
  | 'power-battery'
  | 'opening'
  | 'control'
  | 'color-white'
  | 'color-black'
  | 'color-grey'
  | 'color-silver'
  | 'protocol'
  | 'size'
  | 'neutral';

const ACCENTS: Record<ConfiguratorAccentKey, { border: string; bg: string; text: string; glow: string }> = {
  'tier-standard': {
    border: 'rgba(246, 173, 85, 0.65)',
    bg: 'rgba(246, 173, 85, 0.14)',
    text: '#F6AD55',
    glow: '0 0 18px rgba(246, 173, 85, 0.28)',
  },
  'tier-premium': {
    border: 'rgba(186, 104, 255, 0.6)',
    bg: 'rgba(186, 104, 255, 0.14)',
    text: '#CE93D8',
    glow: '0 0 18px rgba(186, 104, 255, 0.28)',
  },
  'power-wired': {
    border: 'rgba(255, 213, 79, 0.85)',
    bg: 'rgba(255, 213, 79, 0.12)',
    text: '#FFE082',
    glow: '0 0 20px rgba(255, 213, 79, 0.35)',
  },
  'power-battery': {
    border: 'rgba(129, 199, 132, 0.85)',
    bg: 'rgba(129, 199, 132, 0.14)',
    text: '#A5D6A7',
    glow: '0 0 20px rgba(129, 199, 132, 0.35)',
  },
  opening: {
    border: 'rgba(99, 179, 237, 0.75)',
    bg: 'rgba(99, 179, 237, 0.16)',
    text: 'var(--cyan-bright)',
    glow: '0 0 18px rgba(99, 179, 237, 0.3)',
  },
  control: {
    border: 'rgba(99, 179, 237, 0.7)',
    bg: 'rgba(99, 179, 237, 0.14)',
    text: 'var(--cyan-bright)',
    glow: '0 0 16px rgba(99, 179, 237, 0.25)',
  },
  'color-white': {
    border: 'rgba(255, 255, 255, 0.92)',
    bg: 'rgba(255, 255, 255, 0.1)',
    text: '#FFFFFF',
    glow: '0 0 16px rgba(255, 255, 255, 0.22)',
  },
  'color-black': {
    border: 'rgba(18, 18, 22, 0.95)',
    bg: 'rgba(0, 0, 0, 0.45)',
    text: '#E0E0E0',
    glow: '0 0 14px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.15)',
  },
  'color-grey': {
    border: 'rgba(158, 158, 158, 0.85)',
    bg: 'rgba(158, 158, 158, 0.14)',
    text: '#BDBDBD',
    glow: '0 0 14px rgba(158, 158, 158, 0.25)',
  },
  'color-silver': {
    border: 'rgba(192, 192, 200, 0.85)',
    bg: 'rgba(192, 192, 200, 0.12)',
    text: '#E0E0E8',
    glow: '0 0 16px rgba(192, 192, 200, 0.3)',
  },
  protocol: {
    border: 'rgba(99, 179, 237, 0.65)',
    bg: 'rgba(99, 179, 237, 0.12)',
    text: 'var(--cyan-bright)',
    glow: '0 0 14px rgba(99, 179, 237, 0.22)',
  },
  size: {
    border: 'rgba(99, 179, 237, 0.8)',
    bg: 'rgba(99, 179, 237, 0.2)',
    text: 'var(--cyan-bright)',
    glow: '0 0 22px rgba(99, 179, 237, 0.32)',
  },
  neutral: {
    border: 'var(--border-subtle)',
    bg: 'rgba(255,255,255,0.04)',
    text: 'var(--text-2)',
    glow: 'none',
  },
};

export function chipAccentStyle(key: ConfiguratorAccentKey, active: boolean): CSSProperties {
  const a = ACCENTS[key] ?? ACCENTS.neutral;
  if (!active) {
    return {
      background: ACCENTS.neutral.bg,
      color: ACCENTS.neutral.text,
      border: `1px solid ${ACCENTS.neutral.border}`,
      boxShadow: 'none',
      transition: 'border-color 220ms ease, box-shadow 280ms ease, background 220ms ease, color 220ms ease',
    };
  }
  return {
    background: a.bg,
    color: a.text,
    border: `1.5px solid ${a.border}`,
    boxShadow: a.glow,
    transition: 'border-color 220ms ease, box-shadow 280ms ease, background 220ms ease, color 220ms ease',
  };
}

export function colorAccentKey(label: string): ConfiguratorAccentKey {
  const n = label.trim().toLowerCase().replace(/ё/g, 'е');
  if (n.includes('бел')) return 'color-white';
  if (n.includes('черн')) return 'color-black';
  if (n.includes('сереб')) return 'color-silver';
  if (n.includes('сер')) return 'color-grey';
  return 'neutral';
}

export function tierAccentKey(slug: string): ConfiguratorAccentKey {
  const n = slug.toLowerCase();
  if (n.includes('premium')) return 'tier-premium';
  return 'tier-standard';
}

export function tierPillLabel(slug: string): string {
  const n = slug.toLowerCase();
  if (n.includes('premium')) return 'Premium';
  if (n.includes('standard')) return 'Standard';
  return slug;
}

/** Pill/summary chip — same colors as PROTOCOL_THEME on the configurator tiles. */
export function chipStyleForPillLabel(label: string): CSSProperties {
  const direct = PROTOCOL_THEME[label];
  if (direct) {
    return {
      background: direct.bg,
      color: direct.text,
      border: `1.5px solid ${direct.border}`,
      boxShadow: `0 0 16px ${direct.border}`,
    };
  }
  const n = label.trim().toLowerCase().replace(/ё/g, 'е');
  if (n.includes('zigbee')) return chipStyleForPillLabel('ZigBee');
  if (n.includes('tuya') || n.includes('smart life')) return chipStyleForPillLabel('Wi-Fi (TUYA)');
  if (n.includes('matter')) return chipStyleForPillLabel('Matter');
  if (n.includes('485')) return chipStyleForPillLabel('RS-485');
  if (n.includes('фаз')) return chipStyleForPillLabel('Фаза');
  if (n.includes('сухой')) return chipStyleForPillLabel('Сухой контакт');
  if (n.includes('пульт')) return chipStyleForPillLabel('Пульт ДУ');
  return chipAccentStyle('control', true);
}
